import { CheckCircle } from 'lucide-react';
import { useForm, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { useMemo, useState, type SubmitEventHandler } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Callout, Flex, Heading, Text, TextField } from '@radix-ui/themes';
import { Button, FieldError, ServerErrorCallout } from '@/components/common';
import { WizardHeader, WizardScreen } from '@/components/layout';
import { analyticsClient } from '@/lib';
import { authService } from '@/services';
import { fieldLabelStyle } from './styles';

type Translate = (key: string) => string;

const createForgotPasswordSchema = (t: Translate) =>
  z.object({
    email: z
      .string()
      .min(1, t('errors.emailRequired'))
      .check(z.email(t('errors.emailInvalid'))),
  });

type ForgotPasswordFormValues = z.infer<ReturnType<typeof createForgotPasswordSchema>>;

const ForgotPasswordForm = ({
  emailLabel,
  emailPlaceholder,
  submitLabel,
  disabled,
  register,
  errors,
  onSubmit,
}: {
  emailLabel: string;
  emailPlaceholder: string;
  submitLabel: string;
  disabled: boolean;
  register: UseFormRegister<ForgotPasswordFormValues>;
  errors: FieldErrors<ForgotPasswordFormValues>;
  onSubmit: SubmitEventHandler<HTMLFormElement>;
}) => (
  <form onSubmit={onSubmit} noValidate>
    <Flex direction="column" gap="4">
      <Flex direction="column" gap="1">
        <Text size="1" color="gray" style={fieldLabelStyle}>
          {emailLabel}
        </Text>
        <TextField.Root
          type="email"
          placeholder={emailPlaceholder}
          aria-label={emailLabel}
          aria-invalid={errors.email ? true : undefined}
          disabled={disabled}
          {...register('email')}
        />
        <FieldError message={errors.email?.message} />
      </Flex>

      <Button type="submit" disabled={disabled} fullWidth size="lg">
        {submitLabel}
      </Button>
    </Flex>
  </form>
);

/** v90 s-forgot-password — request a Firebase password-reset email. */
export function ForgotPasswordPage() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const schema = useMemo(() => createForgotPasswordSchema(key => t(key)), [t]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const handleForgot = async (data: ForgotPasswordFormValues) => {
    setError(null);
    setLoading(true);
    try {
      await authService.forgotPassword(data.email);
      analyticsClient.capture('password_reset_requested');
      setSent(true);
    } catch (err: unknown) {
      console.error(err);
      setError(err instanceof Error ? t(err.message) : t('errors.forgotPasswordFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <WizardScreen header={<WizardHeader onBack={() => navigate('/login')} />}>
      <Flex direction="column" gap="4" py="2">
        <Heading size="5" weight="light">
          {t('forgotPassword.title')}
        </Heading>

        <Text size="2" color="gray">
          {t('forgotPassword.description')}
        </Text>

        {error ? <ServerErrorCallout message={error} /> : null}

        <ForgotPasswordForm
          emailLabel={t('forgotPassword.emailLabel')}
          emailPlaceholder={t('forgotPassword.emailPlaceholder')}
          submitLabel={t('forgotPassword.submit')}
          disabled={loading || sent}
          register={register}
          errors={errors}
          onSubmit={handleSubmit(handleForgot)}
        />

        {sent && (
          <Callout.Root color="green" variant="surface" size="1" style={{ alignItems: 'center' }}>
            <Callout.Icon>
              <CheckCircle size={16} />
            </Callout.Icon>
            <Callout.Text size="1" style={{ margin: 0 }}>
              {t('forgotPassword.sent')}
            </Callout.Text>
          </Callout.Root>
        )}
      </Flex>
    </WizardScreen>
  );
}
